'use client';

import { useRef } from 'react';

import { useSearchParams } from 'next/navigation';

import { LanguageEnum, TalentLabel } from '@/generated/graphql/types';

import { defaultModulesNames } from '../../constants';
import { TTalentUser } from '../useGetUserData';
import { useTalentGetLabelsTranslationQuery } from './graphql/GetModuleNames.v5.generated';

// hook props
type TOptions = {
  companyId?: number;
  user?: TTalentUser | null;
};

/* default talent module names - to view all the list go to:
https://docs.google.com/spreadsheets/d/1PxvFUXQZHrCbUZu6_RoRMw_RWuOQX0IvL9WqMqUUVMo/edit?gid=1795599488#gid=1795599488
*/
const defaultModuleNames: TalentLabel[] = defaultModulesNames;

export const useGetModuleNames = ({ companyId, user }: TOptions) => {
  /* obtains companyId from query params */
  const searchParams = useSearchParams();
  const companyIdRef = useRef(
    parseInt(searchParams.get('companyId') as string),
  );
  const moduleNamesResponse = useTalentGetLabelsTranslationQuery({
    variables: {
      input: {
        company_id: companyId || companyIdRef.current,
        labels: defaultModuleNames.map(label => {
          return label.label;
        }),
        language: user?.language || user?.company?.language || LanguageEnum.Es,
      },
    },
    skip: !user,
    context: { clientName: 'v5' },
  });

  /* maps error and return default modules names */
  if (
    moduleNamesResponse.error ||
    moduleNamesResponse.data?.talent?.talentHCM?.get_labels_translation
      ?.__typename === 'ResponseInternalError' ||
    moduleNamesResponse.data?.talent?.talentHCM?.get_labels_translation
      ?.__typename === 'ResponseValidationError'
  ) {
    return {
      ...moduleNamesResponse,
      moduleNames: defaultModuleNames,
    };
  }

  /* success response */
  if (
    moduleNamesResponse.data?.talent?.talentHCM?.get_labels_translation
      ?.__typename === 'TalentLabelList'
  ) {
    const moduleNames: TalentLabel[] = [
      ...moduleNamesResponse.data.talent.talentHCM.get_labels_translation
        .labels,
    ];

    return {
      ...moduleNamesResponse,
      moduleNames,
    };
  }

  /* default response */
  return {
    ...moduleNamesResponse,
    moduleNames: defaultModuleNames,
  };
};
